import { RSAA } from 'redux-api-middleware'
import { makeQueryParams } from 'sidecar-utility-library'

import { types, initialState } from 'store/Fills'

/**
 * The SmithRx fill APIs live behind the API gateway (BasePath
 * `pharmacy-fills`), NOT the standard
 * `${env}-api.sidecarhealth.com/{service}/v1` host that `getApiPath` builds.
 * Mirror the env-keyed gateway map already used by
 * `store/Expense/AIFollowUpQuestions` and `Receipt`.
 *
 * @returns {string} Gateway base URL for the pharmacy-fills service.
 */
const getFillsGatewayBase = () => {
  const environmentBases = {
    prod: 'https://prod-api-gateway.sidecarhealth.com/pharmacy-fills',
    qa: 'https://qa-api-gateway.sidecarhealth.com/pharmacy-fills',
    dev: 'https://dev-api-gateway.sidecarhealth.com/pharmacy-fills',
  }

  return environmentBases[process.env.APP_ENV] || environmentBases.dev
}

/**
 * Fetch candidate fills for the member's expense-submission picker.
 *
 * `member_uuid` is both the row filter and the PHI authorization axis — omit it
 * and the gateway returns 403. `matched_within_days` bounds only
 * already-matched fills; unmatched fills are returned regardless of age.
 *
 * @param {object} params
 * @param {string} params.memberUuid - Member whose fills to fetch.
 * @param {number} [params.matchedWithinDays] - Age bound for matched fills.
 * @param {boolean} [params.excludeReversed] - Ask for unreversed fills only
 *   (`reversed=false`). Omitted, the service returns reversed fills too, which
 *   an expense's own matched fills still need.
 * @returns {object} RSAA action
 */
export const fetchFills = ({
  memberUuid,
  matchedWithinDays = 15,
  excludeReversed = false,
}) => ({
  [RSAA]: {
    endpoint: `${getFillsGatewayBase()}/fills${makeQueryParams({
      member_uuid: memberUuid,
      matched_within_days: matchedWithinDays,
      ...(excludeReversed && { reversed: false }),
    })}`,
    method: 'GET',
    types: [
      types.FETCH_FILLS_REQUEST,
      types.FETCH_FILLS_SUCCESS,
      types.FETCH_FILLS_ERROR,
    ],
  },
})

/** Clear fetched candidate fills (e.g. when the picker unmounts). */
export const clearFills = () => ({ type: types.CLEAR_FILLS })

export default (state = initialState, { type, payload = {} }) => {
  switch (type) {
    case types.FETCH_FILLS_REQUEST:
      return {
        ...state,
        fills: { ...state.fills, busy: true, error: null },
      }

    case types.FETCH_FILLS_SUCCESS:
      return {
        ...state,
        fills: {
          ...state.fills,
          busy: false,
          // Fail closed to an empty list on a malformed payload: `?? []` only
          // guards null/undefined, so a non-array `items` would flow through
          // and throw in the picker's `.filter` calls.
          entities: Array.isArray(payload?.items) ? payload.items : [],
        },
      }

    case types.FETCH_FILLS_ERROR:
      return {
        ...state,
        fills: {
          ...state.fills,
          busy: false,
          error: payload?.response?.message ?? 'Unable to load prescriptions.',
        },
      }

    case types.CLEAR_FILLS:
      return { ...state, fills: { ...initialState.fills } }

    default:
      return state
  }
}
